import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { transparentize } from 'polished';
import { IndexLayoutContent, RedirectBadge, SignUpForm, routes } from '@sketch/modules-common';
import { useForDesktop, Link } from '@sketch/components';
import { isMac } from '@sketch/utils';
import { gqlTintToLocalTint } from './utils.js';
import { useSignUpFormContextForAnalytics } from './useSignUpFormContextForAnalytics.js';
import { RelatedContentLink, RelatedContentImg, RelatedContentTitle, RelatedContentDescription, FullWidthImage, StyledGrid, StyledColumn, Title, Subtitle, Feature, FeatureImg, OperatingSystemWarning, Line, TextSignUp } from './SignUpView.styles.js';
import { tintColors } from '@sketch/global-styles';

const RelatedContent = ({ content }) => {
    const { relatedContent } = content;
    if (!relatedContent) {
        return null;
    }
    return (jsxs(RelatedContentLink, Object.assign({ target: "_blank", href: relatedContent.url, external: true }, { children: [jsx(RelatedContentImg, { src: relatedContent.thumbnail.src, alt: relatedContent.thumbnail.alt }), jsxs("div", { children: [jsx(RelatedContentTitle, { children: relatedContent.title }), jsx(RelatedContentDescription, { children: relatedContent.description })] })] })));
};
const HeroImage = ({ content }) => {
    const { heroImage } = content;
    if (!heroImage) {
        return null;
    }
    return jsx(FullWidthImage, { src: heroImage.src, alt: heroImage.alt });
};
const gutter = {
    xl: 64,
    lg: 64,
    md: 40,
    sm: 32,
    xs: 16,
};
const BaseSignUpView = ({ content, email, professionalRole, invitedUser, onCompleted, onRoleChange = () => { }, useOverrideLayoutProps, }) => {
    const { state } = useLocation();
    const isDesktopAndBigger = useForDesktop();
    const showOSWarning = !isMac();
    const tint = gqlTintToLocalTint[content.tint];
    const getTheme = useCallback((theme) => overriddenThemeFor(theme, tint), [tint]);
    useOverrideLayoutProps({ variant: tint, maxContainerWidth: '960px' });
    const formContextForAnalytics = useSignUpFormContextForAnalytics();
    return (jsxs(IndexLayoutContent, Object.assign({ center: "horizontal", marginTop: true, paddingHorizontal: true }, { children: [(state === null || state === void 0 ? void 0 : state.from) && jsx(RedirectBadge, Object.assign({}, state)), jsxs(StyledGrid, Object.assign({ gutter: gutter }, { children: [jsxs(StyledColumn, Object.assign({ xs: 12, md: 6 }, { children: [jsx(Title, { children: content.title }), isDesktopAndBigger && jsx(Subtitle, { children: content.introduction }), content.features.map((feature, index) => (jsxs(Feature, { children: [jsx(FeatureImg, { src: feature.icon.src, alt: feature.icon.alt }), jsx("span", { children: feature.description })] }, index))), isDesktopAndBigger && (jsxs(Fragment, { children: [jsx(RelatedContent, { content: content }), jsx(HeroImage, { content: content })] }))] })), jsx(StyledColumn, Object.assign({ xs: 12, md: 6 }, { children: jsxs(ThemeProvider, Object.assign({ theme: getTheme }, { children: [jsx(SignUpForm, { email: email, professionalRole: professionalRole, invitedUser: invitedUser, onCompleted: onCompleted, onRoleChange: onRoleChange, callToAction: content.callToAction, formContextForAnalytics: formContextForAnalytics }), showOSWarning && (jsx(OperatingSystemWarning, Object.assign({ "data-testid": "os-warning" }, { children: "To design with Sketch you'll need macOS Big Sur (11.0.0) or later" }))), jsx(Line, {}), jsxs(TextSignUp, { children: ["Already have a Sketch account?", ' ', jsx(Link, Object.assign({ variant: "primary", to: {
                                                pathname: routes.SIGN_IN.create({}),
                                                state,
                                            }, isUnderlined: true }, { children: "Sign in" }))] })] })) }))] }))] })));
};
const overriddenThemeFor = (theme, tint) => {
    if (tint === 'orange') {
        return theme;
    }
    const primaryColor = primaryColorFor(tint);
    const secondaryColor = transparentize(0.5, primaryColor);
    return Object.assign(Object.assign({}, theme), { colors: Object.assign(Object.assign({}, theme.colors), { sketch: Object.assign(Object.assign({}, theme.colors.sketch), { A: primaryColor, B: primaryColor, C: secondaryColor, E: secondaryColor }) }) });
};
const primaryColorFor = (tint) => {
    switch (tint) {
        case 'teal':
            return tintColors[5];
        case 'purple':
            return tintColors[8];
        case 'green':
            return tintColors[4];
        case 'beige':
            return tintColors[3];
    }
};

export { BaseSignUpView as default };
